import type { SimulationListView } from '@nexelem/graphne-api';
import { Drawer, createDrawer } from '@nexelem/drawer';
import { Skeleton, Button } from '@nexelem/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { useCustomTranslation } from '../../../../../../hooks';
import { generatePath, useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../../../../routes/config';
import { useSimulationMeta } from '../../hooks';
import { SimulationTitle } from '../../components/SimulationTitle';
import { useDateFormatter } from '@nexelem/utils';

export type SimulationListViewWithId = SimulationListView & { id: number };

type SimulationDetailsDrawerProps = {
  item: SimulationListViewWithId;
};

const SimulationDetailsDrawer = ({ item }: SimulationDetailsDrawerProps) => {
  const { tWrapper } = useCustomTranslation('products.simulationPlan');
  const navigate = useNavigate();
  const { data: meta, isLoading } = useSimulationMeta(item.id.toString());
  const dateFormatter = useDateFormatter({ displayWithHours: true });

  return (
    <Drawer>
      <Drawer.Header title={<SimulationTitle id={item.id} />} onClose={closeSimulationDetailsDrawer} />
      <Drawer.Content>
        <div className="flex w-full flex-col gap-2">
          {isLoading ? (
            Array.from({ length: 5 }, (_, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: static loading placeholder list with fixed order
              <Skeleton className="h-8" key={index} />
            ))
          ) : meta?.parameters?.length ? (
            meta.parameters.map((param) => {
              const value = param.parameterType === 'Date' ? dateFormatter(param.value) : param.value;
              return (
                <div
                  className="flex items-center justify-between gap-5 rounded-xs border border-border-container bg-surface-container-lighter px-6 py-4"
                  key={param.id}
                >
                  <span className="text-s18">{param.name}</span>
                  <span className="min-w-45 text-s18">{value}</span>
                </div>
              );
            })
          ) : (
            <span className="text-s18">{tWrapper('noParameters')}</span>
          )}
        </div>
      </Drawer.Content>
      <Drawer.Footer>
        <Button
          StartIcon={<FontAwesomeIcon icon={faArrowRight} />}
          variant="outlined"
          onClick={() => navigate(generatePath(ROUTES.plan.simulationPath, { id: item.id.toString() }))}
        >
          {tWrapper('simulationLoadButton')}
        </Button>
      </Drawer.Footer>
    </Drawer>
  );
};

export const { toggleSimulationDetailsDrawer, closeSimulationDetailsDrawer } = createDrawer(SimulationDetailsDrawer, {
  name: 'SimulationDetailsDrawer',
  slot: 'right',
  width: 620,
  drawerId: (props: SimulationDetailsDrawerProps) => props.item.id,
});
